import { Features } from "@KassaroAB/shared/types/enums/Features";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { ProductType } from "@KassaroAB/shared/types/enums/ProductType";
import { TABLE_PRODUCT_TYPES } from "./productTableColumns";
import {
  isValidMoneyAmountInputDraft,
  moneyAmountInputToMinor,
} from "../../../utils/parseMoneyAmountInput";
import type { RestUpdateProduct } from "@KassaroAB/shared/types/product";
import colors from "@styling/colors";
import { borderRadius, spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { Button } from "../../../components/Button";
import { Input } from "../../../components/Input";
import { SelectInput } from "../../../components/SelectInput";
import { useProductsTable } from "./productsTableContext";

const Bar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${spacing.x1}px;
  padding: ${spacing.x1}px ${spacing.x2}px;
  background-color: ${colors.infoBackground};
  border-bottom: 1px solid ${colors.border};
`;

const Count = styled.span`
  font-weight: 600;
  font-size: ${fontSizes.small}px;
  color: ${colors.text};
`;

const FieldWrapper = styled.div`
  min-width: 140px;
`;

const CompactSelect = styled(SelectInput)`
  font-size: ${fontSizes.small}px;

  & > button {
    padding: ${spacing.x1}px ${spacing.x3}px ${spacing.x1}px ${spacing.x2}px;
  }
`;

const CompactInput = styled(Input)`
  padding: ${spacing.x1}px ${spacing.x2}px;
  font-size: ${fontSizes.small}px;
`;

const Spacer = styled.div`
  flex: 1;
`;

const DangerButton = styled.button`
  border: 1px solid ${colors.danger};
  background: transparent;
  color: ${colors.danger};
  cursor: pointer;
  font-size: ${fontSizes.small}px;
  font-weight: 600;
  border-radius: ${borderRadius.regular}px;
  padding: ${spacing.x1}px ${spacing.x2}px;

  &:hover {
    background-color: ${colors.danger};
    color: ${colors.dangerText};
  }
`;

const LinkButton = styled.button`
  border: none;
  background: transparent;
  color: ${colors.primary};
  cursor: pointer;
  font-size: ${fontSizes.small}px;
`;

type BulkField = "VAT" | "type" | "groupId" | "unitPrice";

export const ProductBulkEditBar = () => {
  const { t } = useTranslation();
  const { groups, hasFeature, currency, filters, edits, onDeleteSelected } =
    useProductsTable();
  const [field, setField] = useState<BulkField>("VAT");
  const [value, setValue] = useState("");

  const apply = () => {
    let changes: RestUpdateProduct;
    if (field === "VAT") {
      changes = { VAT: Number(value) };
    } else if (field === "type") {
      changes = { type: value as ProductType };
    } else if (field === "groupId") {
      changes = { groupId: value ? value : null };
    } else {
      const minor = moneyAmountInputToMinor(value);
      if (minor === null) {
        return;
      }
      changes = { unitPrice: { amount: minor, currency } };
    }
    edits.applyToSelected(changes);
  };

  return (
    <Bar>
      <Count>
        {t("productsView.bulkSelected", { count: filters.selectedIds.length })}
      </Count>

      <FieldWrapper>
        <CompactSelect
          value={field}
          onChange={(event) => {
            setField(event.target.value as BulkField);
            setValue("");
          }}
        >
          <option value="VAT">{t("productsView.tableVat")}</option>
          <option value="type">{t("productsView.tableType")}</option>
          {hasFeature([Features.GROUPS]) && (
            <option value="groupId">{t("productsView.tableGroup")}</option>
          )}
          <option value="unitPrice">{t("productsView.tableUnitPrice")}</option>
        </CompactSelect>
      </FieldWrapper>

      <FieldWrapper>
        {field === "type" ? (
          <CompactSelect
            value={value}
            onChange={(event) => setValue(event.target.value)}
          >
            <option value="">–</option>
            {TABLE_PRODUCT_TYPES.map((type) => (
              <option key={type} value={type}>
                {t(`productForm.type${type}`)}
              </option>
            ))}
          </CompactSelect>
        ) : field === "groupId" ? (
          <CompactSelect
            value={value}
            onChange={(event) => setValue(event.target.value)}
          >
            <option value="">–</option>
            {groups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </CompactSelect>
        ) : field === "unitPrice" ? (
          <CompactInput
            type="text"
            inputMode="decimal"
            value={value}
            onChange={(event) => {
              const next = event.target.value;
              if (isValidMoneyAmountInputDraft(next)) {
                setValue(next);
              }
            }}
            placeholder={t("productsView.bulkValuePlaceholder")}
          />
        ) : (
          <CompactInput
            type="number"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            placeholder={t("productsView.bulkValuePlaceholder")}
          />
        )}
      </FieldWrapper>

      <Button type="button" $variant="outlined" $small onClick={apply}>
        {t("productsView.bulkApply")}
      </Button>

      <Spacer />

      <DangerButton type="button" onClick={onDeleteSelected}>
        {t("productsView.bulkDelete")}
      </DangerButton>

      <LinkButton type="button" onClick={() => filters.setRowSelection({})}>
        {t("productsView.bulkClear")}
      </LinkButton>
    </Bar>
  );
};
